// src/components/AiDashboard/AiMyNutritionStatus/AiUser/AiUserInputSeparator/AiMealApiCall.js
// Minimal JS GeminiNutritionService used by AiUserInputNutritionData.jsx
// Now returns full macro + vitamin + mineral + fat quality data per region with strict JSON contract,
// robust JSON parsing, and explicit portion and per-100g label-style data.

export const NUTRITION_REFERENCE_META = {
  macros: {
    ranges_Epct: {
      // NNR2023, adults 2+ years
      protein: { min: 10, max: 20, source: 'NNR2023' },
      fat: { min: 25, max: 40, saturatesMax: 10, source: 'NNR2023' },
      carbs: { min: 45, max: 60, addedSugarMax: 10, source: 'NNR2023' }
    },
    // NNR2023 Box 6: at least 3 g/MJ for adults
    fibrePerMJ_g: { min: 3, source: 'NNR2023' }
  },
  bmi: {
    unit: 'kg/m²'
  },
  energy: {
    unit: { BEE: 'MJ/day' }
  },
  // NNR2023 protein: AR 0.66 g/kg, RI 0.83 g/kg for adults
  proteinByKg: {
    AR_g_per_kg: 0.66,
    RI_g_per_kg: 0.83,
    source: 'NNR2023'
  },
  nutrients: {
    // Units only here; you can later plug in RI/AI values per age/sex if you want.
    vitaminA: { unit: 'µg_RE' },
    vitaminD: { unit: 'µg' },
    vitaminB6: { unit: 'mg' },
    folate: { unit: 'µg' },
    vitaminC: { unit: 'mg' },
    vitaminE: { unit: 'mg a-TE' },
    vitaminK: { unit: 'µg' },
    pantothenicAcid: { unit: 'mg' },
    biotin: { unit: 'µg' },
    vitaminB12: { unit: 'µg' },
    choline: { unit: 'mg' },
    calcium: { unit: 'mg' },
    iron: { unit: 'mg' },
    zinc: { unit: 'mg' },
    copper: { unit: 'µg' },
    phosphorus: { unit: 'mg' },
    potassium: { unit: 'mg' },
    magnesium: { unit: 'mg' },
    iodine: { unit: 'µg' },
    selenium: { unit: 'µg' },
    fluoride: { unit: 'mg' },
    manganese: { unit: 'mg' },
    molybdenum: { unit: 'µg' },
    sodium: { unit: 'g' },
    thiamin: { unit: 'mg/MJ' },
    niacin: { unit: 'NE/MJ' }
  }
};

/**
 * Shared nutrient key definitions
 * - USA: full macro + micro set
 * - EU / NORDIC: EU-style front-of-pack + full micronutrient set
 */

const FAT_QUALITY_KEYS = [
  'mufa_g',
  'pufa_g',
  'ala_g',
  'n3_pufa_g',
  'tfa_g',
  'epa_mg',
  'dha_mg'
];

const CORE_MACRONUTRIENTS = [
  'energy_kcal',
  'protein_g',
  'fat_g',
  'carbohydrates_g',
  'fibre_g'
];

const USA_MICRONUTRIENTS = [
  'sodium_g',
  'potassium_mg',
  'calcium_mg',
  'iron_mg',
  'phosphorus_mg',
  'magnesium_mg',
  'zinc_mg',
  'copper_µg',
  'selenium_µg',
  'manganese_mg',
  'iodine_µg',
  'fluoride_mg',
  'choline_mg',
  'vitaminA_µg_RE',
  'vitaminC_mg',
  'vitaminD_µg',
  'vitaminE_mg_aTE',
  'vitaminK_µg',
  'thiamin_mg',
  'riboflavin_mg',
  'niacin_NE',
  'vitaminB6_mg',
  'folate_µg',
  'vitaminB12_µg',
  'pantothenicAcid_mg',
  'biotin_µg',
  'molybdenum_µg',
  'polyphenols_mg',
  'caffeine_mg'
];

const USA_ADDITIONAL_MACRONUTRIENTS = [
  'cholesterol_mg'
];

const USA_NUTRIENT_KEYS = [
  ...CORE_MACRONUTRIENTS,
  ...USA_ADDITIONAL_MACRONUTRIENTS,
  ...USA_MICRONUTRIENTS,
  ...FAT_QUALITY_KEYS
];

const EU_ADDITIONAL_MACRONUTRIENTS = [
  'energy_kj',
  'saturates_g',
  'sugars_g',
  'salt_g',
  'cholesterol_mg'
];

const EU_MICRONUTRIENTS = [
  'potassium_mg',
  'calcium_mg',
  'iron_mg',
  'phosphorus_mg',
  'magnesium_mg',
  'zinc_mg',
  'copper_µg',
  'selenium_µg',
  'manganese_mg',
  'iodine_µg',
  'fluoride_mg',
  'choline_mg',
  'vitaminA_µg_RE',
  'vitaminC_mg',
  'vitaminD_µg',
  'vitaminE_mg_aTE',
  'vitaminK_µg',
  'thiamin_mg',
  'riboflavin_mg',
  'niacin_NE',
  'vitaminB6_mg',
  'folate_µg',
  'vitaminB12_µg',
  'pantothenicAcid_mg',
  'biotin_µg',
  'molybdenum_µg',
  'caffeine_mg'
];

const EU_NUTRIENT_KEYS = [
  ...CORE_MACRONUTRIENTS,
  ...EU_ADDITIONAL_MACRONUTRIENTS,
  ...EU_MICRONUTRIENTS,
  ...FAT_QUALITY_KEYS
];

// Region style mapping: Nordics reuse EU-style nutrient keys and conversions
const REGION_STYLES = {
  USA: 'USA',
  EU: 'EU',
  NORDIC: 'EU'
};

function normalizeRegionCode(region) {
  if (!region) return 'USA';
  const upper = String(region).toUpperCase();
  if (Object.prototype.hasOwnProperty.call(REGION_STYLES, upper)) {
    return upper;
  }
  return 'USA';
}

function getRegionStyle(region) {
  const normalized = normalizeRegionCode(region);
  return REGION_STYLES[normalized] || 'USA';
}

function getNutrientKeysForRegion(region) {
  return getRegionStyle(region) === 'EU' ? EU_NUTRIENT_KEYS : USA_NUTRIENT_KEYS;
}

function getMicroNutrientKeysForRegion(region) {
  const base =
    getRegionStyle(region) === 'EU' ? EU_MICRONUTRIENTS : USA_MICRONUTRIENTS;
  return [...base, ...FAT_QUALITY_KEYS];
}

/**
 * Region-specific prompt guidance
 */
function getRegionDirective(region) {
  const normalized = normalizeRegionCode(region);
  const style = getRegionStyle(region);

  if (normalized === 'NORDIC') {
    return [
      'Region: NORDIC (Nordic and Baltic countries).',
      'Use Nordic Nutrition Recommendations 2023 (NNR2023) as the primary reference, together with Nordic food composition tables (e.g., Fineli, Swedish Food Agency databases).',
      'Follow EU-style labelling conventions: include both energy_kj and energy_kcal, and report salt_g (grams of salt) rather than sodium_g in the final output.',
      'Include saturates_g and sugars_g when applicable.'
    ].join(' ');
  }

  if (style === 'EU') {
    return [
      'Region: EU.',
      'Use European food composition data (e.g., CoFID, CIQUAL) and EU front-of-pack conventions.',
      'Must include energy_kj and energy_kcal. Use salt_g (grams of salt). Do NOT use sodium_g in the final output.',
      'Include saturates_g and sugars_g when applicable.'
    ].join(' ');
  }

  // Default: USA-style
  return [
    'Region: USA.',
    'Use USDA FoodData Central and US nutrition conventions.',
    'Use sodium_g (grams of sodium). You may omit energy_kj and salt_g unless confidently inferred.'
  ].join(' ');
}

/**
 * Region-specific source description for the output
 */
function getRegionSource(region) {
  const normalized = normalizeRegionCode(region);
  const style = getRegionStyle(region);

  if (normalized === 'NORDIC') {
    return 'Estimated from Nordic food composition databases (e.g., Fineli, Swedish Food Agency) and Nordic Nutrition Recommendations 2023 (NNR2023).';
  }

  if (style === 'EU') {
    return 'Estimated from European food composition databases (e.g., CoFID, CIQUAL).';
  }

  return 'Estimated from USDA FoodData Central profiles and typical serving sizes.';
}

/**
 * Simple event metadata factory for tracing
 */
function createEventMetadata(serviceName, input, timestampIso) {
  const safeTimestampIso =
    typeof timestampIso === 'string' && timestampIso
      ? timestampIso
      : new Date().toISOString();
  const generatedRequestId = defaultUlid();
  const requestId = input?.requestId || generatedRequestId;
  const traceId = input?.traceId || requestId;

  const changeHashInput = JSON.stringify({
    text: input?.text ?? input?.minimalInputJson?.request?.text ?? '',
    region: input?.region || null,
    entityId: input?.entityId || 'user_free_text'
  });

  return {
    source: serviceName,
    ingress: input?.ingress || 'client',
    egress: input?.egress || 'gemini_api',
    requestId,
    traceId,
    visited: [serviceName],
    hopCount: 1,
    publishedAt: safeTimestampIso,
    idempotencyKey: input?.idempotencyKey || null,
    changeHash: fallbackHash(changeHashInput),
    entityId: input?.entityId || 'user_free_text'
  };
}

// -----------------------------------------------------------------------------
// GeminiNutritionService
// -----------------------------------------------------------------------------

export default class GeminiNutritionService {
  constructor(config = {}) {
    this.serviceName = config.serviceName || 'GeminiNutritionService';
    this.model = config.model || 'models/gemini-2.5-flash-lite';
    this.endpoint =
      config.endpoint || 'https://generativelanguage.googleapis.com/v1beta';
    this.apiKeyProvider =
      typeof config.apiKeyProvider === 'function'
        ? config.apiKeyProvider
        : () => null;
    this.httpClient =
      typeof config.httpClient === 'function'
        ? config.httpClient
        : (input, init) => fetch(input, init);
  }

  async requestNutrition(input) {
    const text = String(
      input?.text ?? input?.minimalInputJson?.request?.text ?? ''
    ).trim();

    if (!text) {
      return this._reject('empty_text');
    }

    const apiKey = this.apiKeyProvider();
    if (!apiKey) {
      return this._reject('api_key_missing');
    }

    const region = normalizeRegionCode(input?.region || 'USA');
    const parsePreview = this._parsePreview(
      text,
      input?.minimalInputJson?.parsePreview
    );
    const timestampIso = new Date().toISOString();
    const eventMeta = createEventMetadata(this.serviceName, input, timestampIso);

    const prompt = this._buildPrompt({ text, region, parsePreview });

    const url = `${this.endpoint}/${this.model}:generateContent?key=${encodeURIComponent(
      apiKey
    )}`;

    const body = {
      contents: [{ role: 'user', parts: [{ text: prompt }] }],
      generationConfig: {
        temperature: 0.1,
        topP: 0.1,
        topK: 32,
        maxOutputTokens: 6000
      }
    };

    try {
      const httpResponse = await this.httpClient(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!httpResponse || !httpResponse.ok) {
        return this._reject(`http_${httpResponse?.status || 'error'}`);
      }

      const payload = await httpResponse.json();
      const rawTextOutput = extractTextFromGeminiPayload(payload);
      const parsed = robustParseJson(rawTextOutput);

      if (!parsed || typeof parsed !== 'object') {
        const rejectedResult = this._reject('json_parse_failed');
        rejectedResult.meta = {
          ...(rejectedResult.meta || {}),
          rawText: rawTextOutput,
          event: eventMeta
        };
        return rejectedResult;
      }

      const normalized = this._normalizeResponse(parsed, {
        text,
        region,
        parsePreview,
        fallbackTimestampIso: timestampIso
      });

      return {
        accepted: true,
        reason: null,
        timestampIso: normalized.timestampIso,
        region,
        json: normalized,
        meta: { event: eventMeta }
      };
    } catch (error) {
      return this._reject('unexpected_error');
    }
  }

  /**
   * Parse or reuse preview of the input text segments.
   * If an incoming parsePreview looks broken (for example "iron 0" / "9mg"),
   * we discard it and recompute from the canonical text.
   */
  _parsePreview(text, existingParsePreview) {
    const canonicalText = String(text || '');

    if (existingParsePreview && Array.isArray(existingParsePreview.parts)) {
      const validatedPreview = this._validateExistingParsePreview(
        existingParsePreview
      );

      if (validatedPreview) {
        const count =
          typeof validatedPreview.count === 'number'
            ? validatedPreview.count
            : validatedPreview.parts.length;
        return { parts: validatedPreview.parts, count };
      }
      // If validation fails, fall through and recompute.
    }

    const parts = this._separateIntoParts(canonicalText);
    return { parts, count: parts.length };
  }

  /**
   * Validate an incoming parsePreview.
   * If it has obviously broken numeric fragments (for example "9mg" as its own part),
   * return null to force recomputation.
   */
  _validateExistingParsePreview(existingParsePreview) {
    const rawParts = Array.isArray(existingParsePreview.parts)
      ? existingParsePreview.parts
      : [];

    if (rawParts.length === 0) {
      return null;
    }

    const hasBrokenNumericFragments = rawParts.some((rawPart) => {
      const part = String(rawPart || '').trim();
      if (!part) return false;

      // Numeric-only fragments with optional units, e.g.:
      // "9", "0.9", "9mg", "0.9 mg", "10%", "0.9 kcal"
      const numericFragmentPattern =
        /^[0-9]+(\.[0-9]+)?\s*(mg|mcg|µg|g|kj|kcal|%)?$/i;
      return numericFragmentPattern.test(part);
    });

    if (hasBrokenNumericFragments) {
      return null;
    }

    return {
      parts: rawParts.map((part) => String(part || '').trim()).filter(Boolean),
      count:
        typeof existingParsePreview.count === 'number'
          ? existingParsePreview.count
          : rawParts.length
    };
  }

  /**
   * Compute a fresh segmentation of the user text into logical parts.
   * Strategy:
   *  - Treat newlines and semicolons as the primary separators.
   *  - Optionally break on commas and " and " for meal-style inputs.
   *  - DO NOT split on periods, so decimal values like "0.9mg" stay intact.
   */
  _separateIntoParts(text) {
    const raw = String(text || '');

    const primarySplit = raw
      // Strong separators: newlines and semicolons separate label fields cleanly.
      .split(/\r?\n|;/)
      // Softer separators: commas separate multiple foods in one sentence.
      .flatMap((segment) => segment.split(/,/))
      // " and " for meal-style sentences ("rice and beans").
      .flatMap((segment) => segment.split(/\sand\s/i))
      // Normalize whitespace and drop empty entries.
      .map((segment) => segment.trim())
      .filter(Boolean);

    const seenPartsLowerCase = new Set();
    const uniqueParts = [];

    for (const item of primarySplit) {
      const lowerCaseKey = item.toLowerCase();
      if (seenPartsLowerCase.has(lowerCaseKey)) continue;
      seenPartsLowerCase.add(lowerCaseKey);
      uniqueParts.push(item);
    }

    return uniqueParts;
  }

  _buildPrompt({ text, region, parsePreview }) {
    const header =
      'You are a nutrition assistant. Respond with STRICT JSON ONLY. No prose, no markdown, no comments, no code fences.';

    const regionDirective = getRegionDirective(region);

    const determinism = [
      'Use the provided parsePreview.parts as the canonical segmentation of the meal; if you consolidate items, do it only inside "consumed".',
      'When the text includes explicit nutrient amounts with units (for example "iron 0.9mg", "calcium 260mg"), treat those amounts as ground truth for the corresponding nutrient keys expressed in mg or g.',
      'If the label only provides percent daily values (for example "iron 10%") and no absolute mg amount, you may set the corresponding mg nutrient to 0 if you cannot confidently infer the mg value.',
      'Assume typical household / cafe portions when quantities are unspecified and return totals for the portions, not per-100g values.',
      'For every entry in "consumed", include a "portion" object that describes the portion actually used for the nutrient calculation: "quantity" (numeric), "unit" (for example "g", "ml", "slice", "cup"), "gramEquivalent" (mass of that portion in grams; for liquids you may approximate 1 ml ≈ 1 g), and "isEstimated" (true if you inferred the portion, false if the user specified it).',
      'Use only the nutrient keys in the contract below. For any micronutrient you cannot estimate, set its numeric value to 0 (do not omit the key).',
      'All nutrient values must be numbers (no strings, no units).',
      'Return compact, minified JSON (no pretty-print or explanatory text).'
    ].join(' ');

    const contractText = this._buildContractText(region);

    const inputBlock = JSON.stringify(
      {
        timestampIso: new Date().toISOString(),
        request: { text },
        parsePreview
      },
      null,
      2
    );

    return [
      header,
      regionDirective,
      determinism,
      'OUTPUT CONTRACT (exact keys and types, no extra top-level fields):',
      contractText,
      'INPUT (guidance for segmentation and portion inference):',
      inputBlock
    ].join('\n\n');
  }

  _buildContractText(region) {
    const nutrientKeys = getNutrientKeysForRegion(region);
    const nutrientLines = nutrientKeys
      .map((key) => `        "${key}": number`)
      .join(',\n');

    return `{
  "timestampIso": string,
  "request": { "text": string },
  "parsePreview": { "parts": string[], "count": number },
  "consumed": [
    {
      "id": string,
      "label": string,
      "items": string[],
      "portion": {
        "quantity": number,
        "unit": string,
        "gramEquivalent": number,
        "isEstimated": boolean
      },
      "nutrients": {
${nutrientLines}
      }
    }
  ],
  "consolidated_nutrients": { ...same numeric nutrient keys as in "nutrients", values are sums over all consumed items... },
  "labeling": {
    "portionGramEquivalent": number
  },
  "source": string
}`;
  }

  _normalizeResponse(parsed, context) {
    const { text, region, parsePreview, fallbackTimestampIso } = context;
    const nutrientKeys = getNutrientKeysForRegion(region);
    const microKeys = getMicroNutrientKeysForRegion(region);
    const regionStyle = getRegionStyle(region);

    const output = isPlainObject(parsed) ? { ...parsed } : {};

    const timestampIso =
      typeof output.timestampIso === 'string' && output.timestampIso
        ? output.timestampIso
        : fallbackTimestampIso || new Date().toISOString();

    output.timestampIso = timestampIso;

    output.source = getRegionSource(region);

    if (!isPlainObject(output.request)) {
      output.request = { text };
    } else if (typeof output.request.text !== 'string') {
      output.request.text = text;
    }

    if (!isPlainObject(output.parsePreview)) {
      output.parsePreview = parsePreview;
    } else {
      if (!Array.isArray(output.parsePreview.parts)) {
        output.parsePreview.parts = parsePreview.parts;
      }
      if (typeof output.parsePreview.count !== 'number') {
        output.parsePreview.count = output.parsePreview.parts.length;
      }
    }

    const consumedArray = Array.isArray(output.consumed) ? output.consumed : [];
    const normalizedConsumed = consumedArray.map((item, index) => {
      const normalizedItem = isPlainObject(item) ? { ...item } : {};

      if (typeof normalizedItem.id !== 'string' || !normalizedItem.id) {
        normalizedItem.id = `item_${index + 1}`;
      }

      if (typeof normalizedItem.label !== 'string' || !normalizedItem.label) {
        const part =
          Array.isArray(parsePreview?.parts) && parsePreview.parts[index]
            ? parsePreview.parts[index]
            : text;
        normalizedItem.label = String(part || `Item ${index + 1}`);
      }

      if (!Array.isArray(normalizedItem.items)) {
        normalizedItem.items = [normalizedItem.label];
      }

      const portion = normalizePortion(normalizedItem.portion);
      normalizedItem.portion = portion;

      const nutrients = isPlainObject(normalizedItem.nutrients)
        ? { ...normalizedItem.nutrients }
        : {};

      if (regionStyle === 'EU') {
        if (
          isFiniteNumber(nutrients.energy_kcal) &&
          !isFiniteNumber(nutrients.energy_kj)
        ) {
          nutrients.energy_kj = Math.round(nutrients.energy_kcal * 4.184);
        }
        if (
          isFiniteNumber(nutrients.sodium_g) &&
          !isFiniteNumber(nutrients.salt_g)
        ) {
          nutrients.salt_g = roundTo(nutrients.sodium_g * 2.5, 4);
          delete nutrients.sodium_g;
        }
        ensureKeysWithZero(nutrients, [
          'saturates_g',
          'sugars_g',
          'salt_g',
          ...EU_MICRONUTRIENTS,
          ...FAT_QUALITY_KEYS
        ]);
      } else {
        if (
          isFiniteNumber(nutrients.salt_g) &&
          !isFiniteNumber(nutrients.sodium_g)
        ) {
          nutrients.sodium_g = roundTo(nutrients.salt_g / 2.5, 4);
        }
        ensureKeysWithZero(nutrients, [
          ...USA_MICRONUTRIENTS,
          ...FAT_QUALITY_KEYS
        ]);
      }

      nutrientKeys.forEach((key) => {
        if (!Object.prototype.hasOwnProperty.call(nutrients, key)) {
          if (microKeys.includes(key)) {
            nutrients[key] = 0;
          }
        } else if (!isFiniteNumber(nutrients[key])) {
          nutrients[key] = 0;
        }
      });

      normalizedItem.nutrients = nutrients;
      return normalizedItem;
    });

    output.consumed = normalizedConsumed;

    const consolidatedTotals = {};
    nutrientKeys.forEach((key) => {
      let sum = 0;
      for (const item of normalizedConsumed) {
        const value = item && item.nutrients ? item.nutrients[key] : undefined;
        if (isFiniteNumber(value)) {
          sum += value;
        }
      }
      consolidatedTotals[key] = sum;
    });

    if (regionStyle === 'EU') {
      if (
        isFiniteNumber(consolidatedTotals.energy_kcal) &&
        !isFiniteNumber(consolidatedTotals.energy_kj)
      ) {
        consolidatedTotals.energy_kj = Math.round(
          consolidatedTotals.energy_kcal * 4.184
        );
      }
      if (
        isFiniteNumber(consolidatedTotals.sodium_g) &&
        !isFiniteNumber(consolidatedTotals.salt_g)
      ) {
        consolidatedTotals.salt_g = roundTo(
          consolidatedTotals.sodium_g * 2.5,
          4
        );
        delete consolidatedTotals.sodium_g;
      }
      ensureKeysWithZero(consolidatedTotals, [
        'saturates_g',
        'sugars_g',
        'salt_g',
        ...EU_MICRONUTRIENTS,
        ...FAT_QUALITY_KEYS
      ]);
    } else {
      if (
        isFiniteNumber(consolidatedTotals.salt_g) &&
        !isFiniteNumber(consolidatedTotals.sodium_g)
      ) {
        consolidatedTotals.sodium_g = roundTo(
          consolidatedTotals.salt_g / 2.5,
          4
        );
      }
      ensureKeysWithZero(consolidatedTotals, [
        ...USA_MICRONUTRIENTS,
        ...FAT_QUALITY_KEYS
      ]);
    }

    output.consolidated_nutrients = consolidatedTotals;

    const labeling = buildLabelingSummary(
      consolidatedTotals,
      normalizedConsumed,
      nutrientKeys
    );
    output.labeling = labeling;

    return output;
  }

  _reject(reason) {
    return {
      accepted: false,
      reason: String(reason || 'invalid'),
      timestampIso: this._utcMidnightIso(new Date()),
      region: null,
      json: null
    };
  }

  _utcMidnightIso(date) {
    return new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        0,
        0,
        0,
        0
      )
    ).toISOString();
  }
}

// -----------------------------------------------------------------------------
// Helpers for Gemini payload and robust JSON extraction
// -----------------------------------------------------------------------------

function extractTextFromGeminiPayload(payload) {
  const candidate =
    payload && payload.candidates && payload.candidates[0];
  const part =
    candidate &&
    candidate.content &&
    candidate.content.parts &&
    candidate.content.parts[0];
  const text = part && (part.text || (part.inlineData && part.inlineData.data));
  return typeof text === 'string' ? text.trim() : '';
}

function tryParseJson(jsonString) {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    return null;
  }
}

function stripCodeFences(text) {
  if (!text) return text;
  return String(text)
    .replace(/^```(?:json)?/i, '')
    .replace(/```$/i, '')
    .trim();
}

function extractFirstJsonObject(text) {
  if (!text) return null;
  const stringValue = String(text);
  const startIndex = stringValue.indexOf('{');
  if (startIndex === -1) return null;

  let depth = 0;
  for (let index = startIndex; index < stringValue.length; index++) {
    const character = stringValue[index];
    if (character === '{') depth += 1;
    if (character === '}') depth -= 1;
    if (depth === 0) {
      const candidate = stringValue.slice(startIndex, index + 1);
      const object = tryParseJson(candidate);
      if (object && typeof object === 'object') return object;
    }
  }
  return null;
}

function robustParseJson(text) {
  if (!text) return null;

  const direct = tryParseJson(text);
  if (direct) return direct;

  const stripped = stripCodeFences(text);
  const directAfterStrip = tryParseJson(stripped);
  if (directAfterStrip) return directAfterStrip;

  const firstObject = extractFirstJsonObject(stripped);
  if (firstObject) return firstObject;

  const firstBraceIndex = stripped.indexOf('{');
  const lastBraceIndex = stripped.lastIndexOf('}');
  if (firstBraceIndex !== -1 && lastBraceIndex > firstBraceIndex) {
    const candidate = stripped.slice(firstBraceIndex, lastBraceIndex + 1);
    const salvaged = tryParseJson(candidate);
    if (salvaged && typeof salvaged === 'object') {
      return salvaged;
    }
  }

  return null;
}

// -----------------------------------------------------------------------------
// Generic utilities
// -----------------------------------------------------------------------------

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function roundTo(value, places) {
  const decimalPlaces = typeof places === 'number' ? places : 3;
  const factor = Math.pow(10, decimalPlaces);
  return Math.round(value * factor) / factor;
}

function ensureKeysWithZero(object, keys) {
  if (!isPlainObject(object)) return;
  for (const key of keys) {
    if (!isFiniteNumber(object[key])) {
      object[key] = 0;
    }
  }
}

/**
 * Ensure we always have a portion with a gram equivalent for label math
 */
function normalizePortion(rawPortion) {
  const portion = isPlainObject(rawPortion) ? { ...rawPortion } : {};

  let quantity = isFiniteNumber(portion.quantity) ? portion.quantity : null;
  let unit =
    typeof portion.unit === 'string' && portion.unit.trim()
      ? portion.unit.trim()
      : null;
  let gramEquivalent = isFiniteNumber(portion.gramEquivalent)
    ? portion.gramEquivalent
    : null;

  if (quantity === null && gramEquivalent !== null) {
    quantity = gramEquivalent;
  }

  if (quantity === null) {
    quantity = 100;
  }

  if (!unit) {
    unit = 'g';
  }

  if (gramEquivalent === null) {
    if (unit === 'g' || unit === 'gram' || unit === 'grams') {
      gramEquivalent = quantity;
    } else if (
      unit === 'ml' ||
      unit === 'milliliter' ||
      unit === 'milliliters'
    ) {
      gramEquivalent = quantity;
    } else {
      gramEquivalent = quantity;
    }
  }

  const isEstimated =
    typeof portion.isEstimated === 'boolean' ? portion.isEstimated : true;

  return {
    quantity,
    unit,
    gramEquivalent,
    isEstimated
  };
}

/**
 * Build label-style summaries for the dashboard.
 *
 * - consolidatedTotals: sums over ALL items.
 * - We derive:
 *   - portionGramEquivalent        = total grams (food + drinks).
 *   - portionGramEquivalentFood    = grams with energy > 0.
 *   - per100g_meal                 = nutrients per 100 g whole meal.
 *   - per100g_food                 = nutrients per 100 g "food part".
 *   - per100g                      = alias of per100g_food.
 */
function buildLabelingSummary(
  consolidatedTotals,
  consumedItems,
  nutrientKeys,
  options = {}
) {
  const { fallbackToMealWhenNoFoodBasis = true } = options;

  const items = Array.isArray(consumedItems) ? consumedItems : [];

  const allItems = items;
  const energyItems = items.filter((item) => {
    const kcal = item?.nutrients?.energy_kcal;
    return typeof kcal === 'number' && Number.isFinite(kcal) && kcal > 0;
  });

  const gramsAll = allItems.reduce((sum, item) => {
    const grams = item?.portion?.gramEquivalent;
    return sum + (isFiniteNumber(grams) ? grams : 0);
  }, 0);

  const gramsFood = energyItems.reduce((sum, item) => {
    const grams = item?.portion?.gramEquivalent;
    return sum + (isFiniteNumber(grams) ? grams : 0);
  }, 0);

  const per100gMeal = {};
  const per100gFood = {};
  const per100g = {};

  if (gramsAll > 0) {
    const factorMeal = 100 / gramsAll;
    for (const key of nutrientKeys) {
      const value =
        consolidatedTotals && isFiniteNumber(consolidatedTotals[key])
          ? consolidatedTotals[key]
          : 0;
      per100gMeal[key] = roundTo(value * factorMeal, 3);
    }
  } else {
    for (const key of nutrientKeys) {
      per100gMeal[key] = 0;
    }
  }

  if (gramsFood > 0) {
    const factorFood = 100 / gramsFood;
    for (const key of nutrientKeys) {
      const value =
        consolidatedTotals && isFiniteNumber(consolidatedTotals[key])
          ? consolidatedTotals[key]
          : 0;
      per100gFood[key] = roundTo(value * factorFood, 3);
      per100g[key] = per100gFood[key];
    }
  } else if (fallbackToMealWhenNoFoodBasis && gramsAll > 0) {
    for (const key of nutrientKeys) {
      per100gFood[key] = per100gMeal[key];
      per100g[key] = per100gMeal[key];
    }
  } else {
    for (const key of nutrientKeys) {
      per100gFood[key] = 0;
      per100g[key] = 0;
    }
  }

  return {
    portionGramEquivalent: roundTo(gramsAll, 3),
    portionGramEquivalentFood: roundTo(gramsFood, 3),
    per100g,
    per100g_meal: per100gMeal,
    per100g_food: per100gFood
  };
}

function fallbackHash(input) {
  const stringValue = String(input ?? '');
  let hash = 0x811c9dc5;
  for (let index = 0; index < stringValue.length; index++) {
    hash ^= stringValue.charCodeAt(index);
    hash =
      (hash +
        ((hash << 1) +
          (hash << 4) +
          (hash << 7) +
          (hash << 8) +
          (hash << 24))) >>>
      0;
  }
  return `fh_${hash.toString(36)}`;
}

function defaultUlid() {
  const timeComponent = Date.now().toString(36).padStart(9, '0');
  let randomComponent;

  try {
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const randomBytes = crypto.getRandomValues(new Uint8Array(10));
      randomComponent = Array.from(randomBytes)
        .map((byte) => (byte % 36).toString(36))
        .join('');
    } else {
      randomComponent = Math.random().toString(36).slice(2, 12).padEnd(10, '0');
    }
  } catch (error) {
    randomComponent = fallbackHash(String(Math.random())).slice(0, 10);
  }

  return `${timeComponent}${randomComponent}`.slice(0, 19);
}
